'use client';

import type { PaginationLinks, PaginationMeta } from '@eliadmin/core/src/api/api-types';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { iconButtonVariants } from '@eliadmin/core/src/components/dlz/ui/icon-button';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import {
  isGlobalNavigation,
  startGlobalNavigation,
  useGlobalNavigation,
} from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Link from '@eliadmin/core/src/components/link';
import { isPlainPaginationClick, paginationHref } from '@eliadmin/core/src/utils/pagination-link';
import { usePathname, useSearchParams } from 'next/navigation';
import { Fragment, useEffect, useRef, useState, type MouseEvent } from 'react';

export type PaginationProps = {
  links: Required<PaginationMeta['links']>;
  className?: {
    main?: string;
    item?: string;
    replaceItem?: string;
  };
  pageKey?: string;
  shouldReplace?: boolean;
  setPage?: (page: number) => void;
  onPageClicked?: (page: number) => void;
  scrollToElementAfterPageChange?: string;
  // When any of these values change, jump back to page 1
  resetWhen?: unknown[];
};

type PagerPage = { link: PaginationLinks; page: number };
type PagerGap = 'wide' | 'compact' | 'both';

/** „…“ before a page where the wide pager, the phone pager or both skip pages. */
const GAP_VISIBILITY: Record<PagerGap, string> = {
  wide: 'max-sm:hidden',
  compact: 'sm:hidden',
  both: '',
};

/**
 * Page numbers of the Figma „Pager“: the first and the last page and the current page with `around`
 * neighbours on each side (the first or last three pages near the ends); a gap of a single page
 * shows that page instead of „…“.
 */
function pagerWindow(current: number, last: number, around: number) {
  const pages = new Set([1, last]);
  for (let page = current - around; page <= current + around; page++) pages.add(page);
  if (current <= 3) [2, 3].forEach(page => pages.add(page));
  if (current >= last - 2) [last - 1, last - 2].forEach(page => pages.add(page));
  const sorted = [...pages].filter(page => page >= 1 && page <= last).sort((a, b) => a - b);
  return new Set(
    sorted.flatMap((page, i) => (page - sorted[i - 1] === 2 ? [page - 1, page] : [page])),
  );
}

/**
 * Page numbers of the phone pager (Figma 60438:86922 „1 2 … 33“): the first, the current and the
 * last page, at either end with the neighbouring page – at most three pages and two „…“, which fit
 * between the previous / next buttons from a 343 px row.
 */
function compactWindow(current: number, last: number) {
  const pages = new Set([1, current, last]);
  if (current === 1) pages.add(2);
  if (current === last) pages.add(last - 1);
  return new Set([...pages].filter(page => page >= 1 && page <= last));
}

/**
 * Pager items: from `sm` the current page with one neighbour on each side (at most seven items with
 * „…“), on phones the compact window. Both windows render at once and CSS hides the extra items, so
 * the server render already fits.
 */
function pagerItems(pages: PagerPage[]) {
  const last = Math.max(...pages.map(item => item.page));
  const current = pages.find(item => item.link.active)?.page ?? 1;
  const wide = pagerWindow(current, last, 1);
  const compact = compactWindow(current, last);
  let previous: number | undefined;
  let previousCompact: number | undefined;

  return pages
    .filter(item => wide.has(item.page))
    .map(item => {
      const isCompact = compact.has(item.page);
      const wideGap = previous !== undefined && item.page - previous > 1;
      const compactGap =
        isCompact && previousCompact !== undefined && item.page - previousCompact > 1;
      previous = item.page;
      if (isCompact) previousCompact = item.page;
      const gap: PagerGap | undefined =
        wideGap && compactGap ? 'both' : wideGap ? 'wide' : compactGap ? 'compact' : undefined;
      return { ...item, compact: isCompact, gap };
    });
}

export function Pagination({ pageKey = 'page', ...props }: PaginationProps) {
  const { t } = useTranslations();
  const controlled = !!(props.shouldReplace && props.setPage);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const isNavigating = useGlobalNavigation();
  const [clicked, setClicked] = useState<number | null>(null);
  const didInitializeResetWhen = useRef(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
    document.body.scrollTo({ top: 0, behavior: 'smooth' });

    const mainElement = document.querySelector('main');
    if (mainElement instanceof HTMLElement) {
      mainElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const resolvePageFromUrl = (url?: string) => {
    if (!url) return undefined;

    try {
      const urlObject = new URL(
        url,
        typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
      );
      const page = urlObject.searchParams.get('page');
      const parsedPage = page ? Number(page) : undefined;

      return parsedPage && !Number.isNaN(parsedPage) ? parsedPage : undefined;
    } catch {
      return undefined;
    }
  };

  const getUrl = (url?: string) => {
    const page = resolvePageFromUrl(url);

    if (!page) return undefined;

    return paginationHref(pathname, searchParams, page, pageKey);
  };

  const getPageFromLink = (link: PaginationLinks) => {
    const pageFromUrl = resolvePageFromUrl(link.url ?? undefined);
    if (pageFromUrl) return pageFromUrl;

    const parsedLabel = Number(link.label);
    return Number.isNaN(parsedLabel) ? undefined : parsedLabel;
  };

  useEffect(() => {
    if (clicked !== null && !isNavigating) {
      setClicked(null);
      if (
        !props.scrollToElementAfterPageChange ||
        props.scrollToElementAfterPageChange === '__top__'
      ) {
        scrollToTop();
        return;
      }

      const targetElement = props.scrollToElementAfterPageChange
        ? document.getElementById(props.scrollToElementAfterPageChange)
        : null;
      targetElement?.scrollIntoView({ behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clicked, isNavigating]);

  // Reset to first page when resetWhen changes
  useEffect(() => {
    if (!props.resetWhen) return;
    if (!didInitializeResetWhen.current) {
      didInitializeResetWhen.current = true;
      return;
    }

    // Prefer controlled pagination
    if (props.shouldReplace && props.setPage) {
      props.setPage(1);
      props.onPageClicked?.(1);
      setClicked(null);
      return;
    }

    // Read the live URL: another filter effect may have updated it in this commit.
    const currentSearchParams = new URLSearchParams(window.location.search);
    currentSearchParams.delete(pageKey);
    const query = currentSearchParams.toString();
    const nextUrl = `${pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    if (nextUrl !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      // Do not pass Next's internal __NA/_N markers: they bypass router sync.
      window.history.replaceState(null, '', nextUrl);
    }
    props.onPageClicked?.(1);
    setClicked(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(props.resetWhen)]);

  const onItemClick = (
    event: MouseEvent<HTMLAnchorElement>,
    index: number,
    targetPage?: number,
  ) => {
    if (!isPlainPaginationClick(event)) return;
    if (controlled) event.preventDefault();
    if (!targetPage) return;

    if ((searchParams.get(pageKey) ?? '1') === String(targetPage)) {
      event.preventDefault();
      props.onPageClicked?.(targetPage);
      return;
    }

    if (
      !props.scrollToElementAfterPageChange ||
      props.scrollToElementAfterPageChange === '__top__'
    ) {
      scrollToTop();
    }

    if (!isGlobalNavigation() && !props.shouldReplace) {
      startGlobalNavigation();
      setClicked(index);
      props.onPageClicked?.(targetPage);
      return;
    }

    if (props.shouldReplace && props.setPage) {
      props.setPage(targetPage);
      setClicked(index);
      props.onPageClicked?.(targetPage);
    }
  };

  const renderItem = (link: PaginationLinks, index: number, count: number, visibility?: string) => {
    const href = getUrl(link.url ?? undefined);
    const targetPage = getPageFromLink(link);
    const disabled = !href;
    const pending = clicked !== null || isNavigating;
    const isPrevious = index === 0;
    const isNext = index === count - 1;
    const key = `${link.label}-${link.url}-${index}`;

    const className = cn(
      iconButtonVariants({ variant: 'outline' }),
      'disabled:text-[#9096A0]',
      !isPrevious && !isNext && 'dlz-body-m-medium',
      link.active &&
        'border-[#D7191F] bg-[#D7191F] text-white shadow-none hover:border-[#B5141A] hover:bg-[#B5141A] disabled:text-white',
      visibility,
      'ea-pagination__item',
      props.className?.item,
    );
    const content = isPrevious ? (
      <Icon name="caret-left" size={24} />
    ) : isNext ? (
      <Icon name="caret-right" size={24} />
    ) : (
      link.label
    );
    const label = isPrevious ? t('text3529') : isNext ? t('text3530') : undefined;
    const current = link.active ? 'page' : undefined;

    if (disabled || pending) {
      return (
        <button
          key={key}
          type="button"
          disabled
          aria-label={label}
          aria-current={current}
          className={className}
        >
          {clicked === index ? <Spinner /> : content}
        </button>
      );
    }

    return (
      <Link
        key={key}
        href={href}
        prefetch={false}
        prefetchOnHover={!controlled && !link.active}
        aria-label={label}
        aria-current={current}
        onClick={event => onItemClick(event, index, targetPage)}
        className={cn(
          className,
          controlled && ['ea-pagination__item-replace-custom', props.className?.replaceItem],
        )}
      >
        {content}
      </Link>
    );
  };

  const links = props.links;
  if (!links?.length) return null;

  // The API list (up to 17 items, „...“ without URL) always contains the pages the pager shows.
  const pages = links.slice(1, -1).flatMap(link => {
    const page = getPageFromLink(link);
    return page ? [{ link, page }] : [];
  });

  /*
   * Figma „Pager“: outline round previous / next buttons 24 px from the page numbers (52 px
   * circles 8 px apart, the current one bg/brand), skipped pages as „…“. On phones (60438:86922)
   * the pager spans the row with previous / next at its ends; when two „…“ do not fit, they narrow.
   */
  return (
    <nav
      aria-label="Stránkovanie"
      className={cn(
        'flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-6',
        'ea-pagination',
        props.className?.main,
      )}
    >
      {renderItem(links[0], 0, links.length)}
      {pages.length ? (
        <div className="flex min-w-0 items-center gap-2">
          {pagerItems(pages).map(({ link, page, compact, gap }) => (
            <Fragment key={page}>
              {gap ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'dlz-body-m-medium inline-flex h-[52px] w-[52px] min-w-4 items-center justify-center text-[#6B7079]',
                    GAP_VISIBILITY[gap],
                  )}
                >
                  …
                </span>
              ) : null}
              {renderItem(link, links.indexOf(link), links.length, compact ? '' : 'max-sm:hidden')}
            </Fragment>
          ))}
        </div>
      ) : null}
      {links.length > 1
        ? renderItem(links[links.length - 1], links.length - 1, links.length)
        : null}
    </nav>
  );
}
